import { useParams, Link, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { getArticleBySlug } from '@/mocks/seo-articles';
import { mainSiteUrl } from '@/mocks/categories';
import ArtigoNavbar from '@/components/feature/ArtigoNavbar';
import ArtigoFooter from '@/components/feature/ArtigoFooter';

export default function ArtigoDetalhePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const siteUrl = import.meta.env.VITE_SITE_URL;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article = slug ? getArticleBySlug(slug) : undefined;

  if (!article) {
    return (
      <main className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center px-6">
          <h1 className="font-heading text-3xl font-bold text-foreground-900 mb-4">Artigo não encontrado</h1>
          <p className="text-foreground-600 mb-6">O artigo que você procura não está disponível.</p>
          <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm">
            <i className="ri-arrow-left-line"></i>
            Voltar ao Início
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background-50">
      <ArtigoNavbar />

      <article className="pt-24 pb-16">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-primary-500 transition-colors font-medium">Início</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-700">{article.category}</span>
          </nav>

          <header className="mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold mb-5">
              <i className="ri-article-line"></i>
              {article.category}
            </div>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-tight mb-4">
              {article.title}
            </h1>
            <p className="text-lg text-foreground-600 leading-relaxed mb-6">
              {article.description}
            </p>
            <div className="flex items-center gap-3 text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                {article.date}
              </span>
              <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                {article.readTime} de leitura
              </span>
            </div>
          </header>

          <div className="rounded-2xl overflow-hidden mb-12 w-full aspect-[16/9]">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-full object-cover"
            />
          </div>

          <div className="prose-custom">
            {article.sections.map((section, idx) => {
              if (section.type === 'heading') {
                return (
                  <h2 key={idx} className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4 mt-10 first:mt-0">
                    {section.content}
                  </h2>
                );
              }
              if (section.type === 'paragraph') {
                return (
                  <p key={idx} className="text-foreground-700 leading-relaxed mb-4">
                    {section.content}
                  </p>
                );
              }
              if (section.type === 'list' && section.items) {
                return (
                  <ul key={idx} className="space-y-2 mb-4">
                    {section.items.map((item, i) => (
                      <li key={i} className="flex items-start gap-3 text-foreground-700 text-sm">
                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-accent-100 text-accent-600 flex items-center justify-center mt-0.5">
                          <i className="ri-check-line text-xs"></i>
                        </span>
                        {item}
                      </li>
                    ))}
                  </ul>
                );
              }
              return null;
            })}
          </div>

          <div className="bg-primary-500 rounded-2xl p-8 md:p-10 text-center my-12">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-3">
              Pronto para Comprar?
            </h2>
            <p className="text-primary-100 text-base mb-6 max-w-lg mx-auto">
              Acesse o CSSBuy Spreadsheet e encontre os melhores produtos agora mesmo.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center gap-2 px-7 py-3 rounded-full bg-background-50 text-primary-600 font-bold text-sm hover:bg-background-100 transition-colors duration-200 whitespace-nowrap"
              >
                CSSBuy Sheet
                <i className="ri-arrow-right-up-line"></i>
              </a>
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 px-7 py-3 rounded-full border-2 border-primary-300 text-background-50 font-bold text-sm hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
              >
                Voltar ao Início
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>

          <div className="border-t border-background-200 pt-8 mt-12">
            <h3 className="font-heading text-lg font-bold text-foreground-900 mb-4">Continue Explorando</h3>
            <div className="flex flex-wrap gap-3">
              <Link to="/" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 text-sm text-foreground-700 hover:bg-background-200 transition-colors whitespace-nowrap font-medium">
                <i className="ri-home-line"></i>
                Página Inicial
              </Link>
              <button
                onClick={() => { navigate('/'); setTimeout(() => document.getElementById('categorias')?.scrollIntoView({ behavior: 'smooth' }), 100); }}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 text-sm text-foreground-700 hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-grid-line"></i>
                Categorias
              </button>
              <Link to="/artigo-cssbuyspreadsheet-guia-completo-2026" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 text-sm text-foreground-700 hover:bg-background-200 transition-colors whitespace-nowrap">
                <i className="ri-book-open-line"></i>
                Guia Completo
              </Link>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 text-sm text-foreground-700 hover:bg-background-200 transition-colors whitespace-nowrap"
              >
                <i className="ri-shopping-bag-line"></i>
                CSSBuy Sheet
              </a>
            </div>
          </div>

          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'Article',
                headline: article.title,
                description: article.description,
                image: article.image,
                datePublished: '2026-06-01',
                dateModified: '2026-06-27',
                author: { '@type': 'Organization', name: 'CSSBuySpreadsheet' },
                publisher: { '@type': 'Organization', name: 'CSSBuySpreadsheet', logo: { '@type': 'ImageObject', url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/d367cf6745ee47f3fc82c76d3a54c50c.png' } },
                mainEntityOfPage: { '@type': 'WebPage', '@id': `${siteUrl}/artigo/${article.slug}` },
              }),
            }}
          />
        </div>
      </article>

      <ArtigoFooter />
    </main>
  );
}